import { selectedScreen, screenContainingComponent } from './nativeScreens.js'
import { COMPONENT_REGISTRY, availableComponentDefinitions } from './nativeSportsbookManifest'
import { boardFitsHub, reidentifyColumns } from './playerPropBoardModel'
import { builderFitsHub, reidentifyBuilder } from './parlayBuilderModel'

export function sectionDuplicationIssue(manifest, component, registry = COMPONENT_REGISTRY, screenId) {
  const screen = screenId ? selectedScreen(manifest, { screenId }) : screenContainingComponent(manifest, component?.id) ?? selectedScreen(manifest)
  const definition = registry.components.find((item) => item.type === component?.type)
  if (!definition || !definition.policy.removable || definition.policy.required) return 'This section cannot be duplicated.'
  if (component?.type === 'competition_markets' && screen.context?.kind !== 'competition') {
    return 'Competition markets can only be added to a hub screen with a competition context.'
  }
  // A board's columns are its league's provider markets, so it cannot take on
  // another hub's league the way competition markets do.
  if (component?.type === 'player_prop_board' && screen.context?.kind === 'competition' && !boardFitsHub(component.scope, screen.context)) {
    return `This player prop board uses ${component.scope?.competition || 'another league'} markets. Add it to Home or the ${component.scope?.competition || 'matching'} hub, or add a new board to this hub.`
  }
  // A builder's leagues carry their own provider markets, so on a hub every league must be the hub's.
  if (component?.type === 'same_game_parlay_builder' && screen.context?.kind === 'competition' && !builderFitsHub(component, screen.context)) {
    return `This Same Game Parlay+ builder lists ${(component.leagues || []).map((league) => league.competition).join(', ') || 'other leagues'}. Add it to Home, or add a new builder to this hub.`
  }
  if (screen.components.length >= 32) return `This ${screen.context ? 'hub' : 'Home'} has reached its section limit.`
  if (!availableComponentDefinitions(manifest, registry, screen.id).some((item) => item.type === component.type)) {
    return `This ${screen.context ? 'hub' : 'Home'} allows up to ${definition.policy.max_instances} ${definition.display_name} sections.`
  }
  return null
}

export function cardDuplicationIssue(component, registry = COMPONENT_REGISTRY) {
  const cards = registry.components.find((item) => item.type === component?.type)?.editor?.cards
  if (!cards || !Array.isArray(component.cards)) return 'This section does not contain supported cards.'
  return component.cards.length >= cards.max_cards ? `This section allows up to ${cards.max_cards} cards.` : null
}

// Only authored instance identities are renewed. Provider IDs, routes, assets,
// schedules, odds and line references retain their original values.
export function duplicateSection(manifest, componentId, registry = COMPONENT_REGISTRY, uuid = () => crypto.randomUUID()) {
  const screen = screenContainingComponent(manifest, componentId)
  const index = (screen?.components ?? []).findIndex((item) => item.id === componentId)
  const source = screen?.components[index]
  const issue = sectionDuplicationIssue(manifest, source, registry)
  if (issue) throw new Error(issue)
  const next = structuredClone(manifest)
  const component = structuredClone(source)
  component.id = uuid()
  component.slot_id = uuid()
  for (const card of component.cards ?? []) card.id = uuid()
  if (component.collection) component.collection.id = uuid()
  reidentifyColumns(component, uuid)
  reidentifyBuilder(component, uuid)
  if (component.type === 'competition_markets' && screen?.context?.kind === 'competition') {
    component.competition = {
      sport: screen.context.sport,
      name: screen.context.competition,
    }
  }
  screenContainingComponent(next, componentId).components.splice(index + 1, 0, component)
  for (const scene of next.experience?.home_scenes ?? []) {
    const position = (scene.component_order ?? []).indexOf(source.id)
    if (position !== -1) scene.component_order.splice(position + 1, 0, component.id)
    if ((scene.hidden_component_ids ?? []).includes(source.id)) scene.hidden_component_ids.push(component.id)
  }
  return { document: next, component }
}

export function duplicateFeaturedCard(manifest, componentId, cardId, registry = COMPONENT_REGISTRY, uuid = () => crypto.randomUUID()) {
  const next = structuredClone(manifest)
  const component = screenContainingComponent(next, componentId)?.components.find((item) => item.id === componentId)
  const issue = cardDuplicationIssue(component, registry)
  if (issue) throw new Error(issue)
  const index = component.cards.findIndex((item) => item.id === cardId)
  if (index === -1) throw new Error('This card is no longer in the section.')
  const card = { ...structuredClone(component.cards[index]), id: uuid() }
  component.cards.splice(index + 1, 0, card)
  return { document: next, component, card }
}
